import APIService from "./APIService";

/**
 * API service for account owner read-only access to consented member data.
 * All methods require the caller to have the account_admin role and the
 * target member to have granted the appropriate owner-access consent.
 */
const MemberViewAPI = {
  /**
   * Get safety score for a consented member (requires owner_dashboard_access consent).
   * Response: { success, memberUuid, data: { totalScore, safetyLevel, calculatedAt } }
   */
  getMemberSafetyScore: async (memberUuid) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/safety-score`,
      );
      console.log(
        `[MemberViewAPI] Get member safety score response:`,
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[MemberViewAPI] Error getting member safety score:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get risk assessment for a consented member (requires owner_analysis_access consent).
   * Response: { success, memberUuid, data: { riskScore, riskLevel } }
   */
  getMemberRiskAssessment: async (memberUuid) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/risk-assessments`,
      );
      console.log(
        `[MemberViewAPI] Get member risk assessment response:`,
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[MemberViewAPI] Error getting member risk assessment:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get training progress for a consented member (requires owner_dashboard_access consent).
   * Response: { success, memberUuid, trainings: [...], count }
   */
  getMemberTraining: async (memberUuid) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/training`,
      );
      console.log(`[MemberViewAPI] Get member training response:`, response);
      return response;
    } catch (error) {
      console.error("[MemberViewAPI] Error getting member training:", error);
      throw error;
    }
  },

  /**
   * Get aggregated dashboard overview for a consented member.
   * Response: { memberUuid, safetyScore, riskAssessment, trainingSummary }
   */
  getMemberDashboard: async (memberUuid) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/dashboard`,
      );
      console.log(`[MemberViewAPI] Get member dashboard response:`, response);
      return response;
    } catch (error) {
      console.error("[MemberViewAPI] Error getting member dashboard:", error);
      throw error;
    }
  },

  /**
   * Get alerts for a consented member (requires owner_dashboard_access consent).
   * Response: { success, memberUuid, alerts: [...], count }
   */
  getMemberAlerts: async (memberUuid) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/alerts`,
      );
      console.log(`[MemberViewAPI] Get member alerts response:`, response);
      return response;
    } catch (error) {
      console.error("[MemberViewAPI] Error getting member alerts:", error);
      throw error;
    }
  },
  /**
   * Get SMS simulation statistics for a consented member (rolling 90-day window).
   * Response matches shape of /api/v1/simulation/sms-simulation-stats.
   * Requires owner_dashboard_access consent.
   */
  getMemberSmsStats: async (memberUuid) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/sms-simulations`,
      );
      console.log(
        `[MemberViewAPI] Get member SMS simulations response:`,
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[MemberViewAPI] Error getting member SMS simulations:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get email simulation statistics for a consented member (rolling 90-day window).
   * Response matches shape of /api/v1/simulation/email-simulation-stats.
   * Requires owner_dashboard_access consent.
   */
  getMemberEmailStats: async (memberUuid) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/email-simulations`,
      );
      console.log(
        `[MemberViewAPI] Get member email simulations response:`,
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[MemberViewAPI] Error getting member email simulations:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get paginated scam analyses history for a consented member.
   * Requires owner_analysis_access consent.
   * @param {string} memberUuid - The member's UUID
   * @param {string} analysisType - Optional filter: EMAIL_ANALYSIS, SMS_ANALYSIS, IMAGE_ANALYSIS
   * @param {number} page - Zero-based page number (default 0)
   * @param {number} size - Page size (default 20)
   */
  getMemberAnalyses: async (
    memberUuid,
    analysisType = null,
    page = 0,
    size = 20,
  ) => {
    try {
      const params = { page, size };
      if (analysisType) params.analysisType = analysisType;
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/analyses`,
        { params },
      );
      console.log(`[MemberViewAPI] Get member analyses response:`, response);
      return response;
    } catch (error) {
      console.error("[MemberViewAPI] Error getting member analyses:", error);
      throw error;
    }
  },

  /**
   * Get paginated forwarded SMS history for a consented member.
   * Queries forwarded_sms_analysis directly (all entries, not just scams).
   * Requires owner_analysis_access consent.
   * @param {string} memberUuid - The member's UUID
   * @param {number} page - Zero-based page number (default 0)
   * @param {number} size - Page size (default 20)
   */
  getMemberForwardedTexts: async (memberUuid, page = 0, size = 20) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/forwarded-texts`,
        { params: { page, size } },
      );
      console.log(
        `[MemberViewAPI] Get member forwarded texts response:`,
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[MemberViewAPI] Error getting member forwarded texts:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get paginated forwarded email analysis history for a consented member.
   * Queries forwarded_email_analysis directly (all entries, not just scams).
   * Requires owner_analysis_access consent.
   * @param {string} memberUuid - The member's UUID
   * @param {number} page - Zero-based page number (default 0)
   * @param {number} size - Page size (default 20)
   */
  getMemberForwardedEmails: async (memberUuid, page = 0, size = 20) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/forwarded-emails`,
        { params: { page, size } },
      );
      console.log(
        `[MemberViewAPI] Get member forwarded emails response:`,
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[MemberViewAPI] Error getting member forwarded emails:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get paginated uploaded image analyses for a consented member.
   * Response: { success, memberUuid, analyses: [...UploadedImageAnalysis...], totalElements, totalPages, currentPage }
   * @param {string} memberUuid - The member's UUID
   * @param {number} page - Zero-based page number (default 0)
   * @param {number} size - Page size (default 20)
   */
  getMemberUploadedImages: async (memberUuid, page = 0, size = 20) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/uploaded-images`,
        { params: { page, size } },
      );
      console.log(
        `[MemberViewAPI] Get member uploaded images response:`,
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[MemberViewAPI] Error getting member uploaded images:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get paginated social media analyses for a consented member.
   * Response: { success, memberUuid, analyses: [...SocialMediaAnalysis...], totalElements, totalPages, currentPage }
   * @param {string} memberUuid - The member's UUID
   * @param {number} page - Zero-based page number (default 0)
   * @param {number} size - Page size (default 20)
   */
  getMemberSocialMediaAnalyses: async (memberUuid, page = 0, size = 20) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/social-media-analyses`,
        { params: { page, size } },
      );
      console.log(
        `[MemberViewAPI] Get member social media analyses response:`,
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[MemberViewAPI] Error getting member social media analyses:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get paginated reports list for a consented member.
   * Response: { success, memberUuid, reports: [...PersonalShieldReportDTO...], count }
   * Requires owner_analysis_access consent.
   * @param {string} memberUuid - The member's UUID
   * @param {number} page - Zero-based page number (default 0)
   * @param {number} size - Page size (default 10)
   */
  getMemberReports: async (memberUuid, page = 0, size = 10) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/reports`,
        { params: { page, size } },
      );
      console.log(`[MemberViewAPI] Get member reports response:`, response);
      return response;
    } catch (error) {
      console.error("[MemberViewAPI] Error getting member reports:", error);
      throw error;
    }
  },

  /**
   * Get the full detail of a single Shield Report for a consented member.
   * Response: PersonalShieldReportDTO
   * Requires owner_analysis_access consent.
   * @param {string} memberUuid  - The member's UUID
   * @param {string} reportUuid  - The report's UUID
   */
  getMemberReportDetail: async (memberUuid, reportUuid) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/reports/${reportUuid}`,
      );
      console.log(
        `[MemberViewAPI] Get member report detail response:`,
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[MemberViewAPI] Error getting member report detail:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get the weekly Shield Score snapshot for a consented member.
   * Response matches shape of /api/v1/app/reporting/score/current.
   * Requires owner_dashboard_access consent.
   */
  getMemberShieldScore: async (memberUuid) => {
    try {
      const response = await APIService.get(
        `/api/v1/app/owner-access/members/${memberUuid}/shield-score`,
      );
      console.log(
        `[MemberViewAPI] Get member shield score response:`,
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[MemberViewAPI] Error getting member shield score:",
        error,
      );
      throw error;
    }
  },
};

export default MemberViewAPI;
